// src/utils/attenteBE.ts — SCE Issues
// FNC-T de documentation de conception (P&ID / PPL, EDE, HDS) : le BE met à
// jour le document, puis la documentation et le retest suivent (modèle de
// l'appli, utils/escaladeFnct). Tant que l'action du BE est ouverte, les autres
// l'ATTENDENT (Guillaume, 23/10) :
//  - elles restent comptées en retard si leur échéance est passée — mais les
//    listes le disent : « En attente du BE » ;
//  - le rappel du lundi / jeudi (flow 7) ne les relance pas : c'est le BE qu'on
//    relance ;
//  - quand le BE clôt, leurs responsables apprennent que leur tour est venu
//    (utils/notificationsFnct).
// Pur, testable hors ligne.

import type { ActionFnct, Fnct, Jour } from '@/types/fnct.types'
import { CATEGORIE_PROCESS } from '@/config/fnct.config'
import { ajouterJoursOuvres, jourLisible } from './fnct'

/** Catégories dont le plan suit « BE, puis documentation, puis retest » (valeurs de la base, faute comprise). */
export const CATEGORIES_AVEC_BE: readonly string[] = [
  CATEGORIE_PROCESS,
  'Engineering documentation - Electical',
  'Engineering documentation - Automation',
]

/** Une FNC-T P&ID / PPL, EDE ou HDS — ou ouverte depuis un relevé d'écarts P&ID / PPL. */
export function estFnctAvecBE(f: Pick<Fnct, 'categorie' | 'ecarts'>): boolean {
  return CATEGORIES_AVEC_BE.includes(f.categorie) || !!f.ecarts?.trim()
}

/**
 * Le début d'une mise à jour de document par le BE : « Update (the / of) »,
 * « Correct », « Modify », « Correct and update », suivi du document — P&ID (ou
 * PID), PPL, EDE, HDS.
 */
const TEXTE_ACTION_BE = /^(?:(?:correct|modify)(?:\s+and\s+update)?|update)(?:\s+(?:the|of))?\s+(?:p&id|pid|ppl|ede|hds)\b/i

/**
 * L'action du BE : la mise à jour du document, reconnue à son TEXTE seul
 * (Guillaume, 30/10 — plus au nom de Geny ou Morlot : d'autres personnes du BE
 * Process peuvent être choisies, et le flow 7 la reconnaît de même). Le modèle
 * écrit « Update the P&ID and PPL », « Update the EDE », « Update the HDS » ; la
 * base reprise de l'Excel dit aussi « Update PID and PPL », « Update the PID »,
 * « Correct P&ID », « Modify the PPL » (vérifié sur la vraie base le 30/10 : 169
 * reconnues, aucune documentation ni aucun retest pris pour une action du BE).
 */
export function estActionDuBE(a: Pick<ActionFnct, 'description'>): boolean {
  return TEXTE_ACTION_BE.test(a.description.trim())
}

/**
 * L'action du BE qu'attend `a` (encore ouverte), ou null : `a` est ouverte,
 * n'est pas celle du BE, et sa FNC-T suit le modèle « BE d'abord ».
 */
export function beAttendu(a: ActionFnct, f: Pick<Fnct, 'categorie' | 'ecarts'>, actionsDeLaFnct: readonly ActionFnct[]): ActionFnct | null {
  if (a.cloture || !estFnctAvecBE(f) || estActionDuBE(a)) return null
  return actionsDeLaFnct.find((x) => x.id !== a.id && x.fnct.toUpperCase() === a.fnct.toUpperCase() && !x.cloture && estActionDuBE(x)) ?? null
}

export const LIBELLE_ATTENTE_BE = 'En attente du BE'

/** Une mise à jour de document par le BE se fait en 10 jours ouvrés au plus (Guillaume, 28/10). */
export const DELAI_MAX_BE_JOURS_OUVRES = 10

/**
 * Le dernier jour permis pour une action du BE ouverte le `depuis`. Un conseil,
 * pas un blocage : un report ou un cas particulier reste possible, mais on le voit.
 */
export function echeanceMaxBE(depuis: Jour): Jour {
  return ajouterJoursOuvres(depuis, DELAI_MAX_BE_JOURS_OUVRES)
}

/**
 * Le rappel à montrer quand l'échéance d'une mise à jour de document par le BE
 * dépasse ses 10 jours ouvrés ; null sinon (autre action, ou délai tenu).
 */
export function avertissementDelaiBE(
  a: Pick<ActionFnct, 'responsable' | 'description'>,
  echeance: Jour | null,
  depuis: Jour,
): string | null {
  if (!echeance || !estActionDuBE(a)) return null
  const max = echeanceMaxBE(depuis)
  if (echeance <= max) return null
  return `Une mise à jour de document par le BE se fait en ${DELAI_MAX_BE_JOURS_OUVRES} jours ouvrés au plus : au plus tard le ${jourLisible(max)}.`
}
